import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import { usePropertyField } from '@vibesites/runtime/react';
import { getContrastTextColor } from '../utils/luminance';

/**
 * CSS custom properties for solid CTA buttons, driven by `property_color_4`.
 * Spread onto a section root; CTA styles read `--cta-bg` / `--cta-text` with
 * secondary fallbacks, so properties without color 4 keep the secondary fill.
 */
export function useCtaColorVars(): CSSProperties | undefined {
  const color4 = usePropertyField('property_color_4')?.trim();
  return useMemo(() => {
    if (!color4) return undefined;
    return {
      ['--cta-bg' as string]: color4,
      ['--cta-text' as string]: getContrastTextColor(color4),
    } as CSSProperties;
  }, [color4]);
}

/**
 * Re-points `--vibe-color-primary` / `--vibe-color-text-on-primary` at
 * `property_color_4` for a subtree. Used to recolor shared blocks (lead form,
 * tour scheduler) whose buttons read the primary token and can't be edited.
 */
export function useColor4AsPrimaryVars(): CSSProperties | undefined {
  const color4 = usePropertyField('property_color_4')?.trim();
  return useMemo(() => {
    if (!color4) return undefined;
    return {
      ['--vibe-color-primary' as string]: color4,
      ['--vibe-color-text-on-primary' as string]: getContrastTextColor(color4),
    } as CSSProperties;
  }, [color4]);
}
